import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Typography } from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import authService from '../../services/authService';

const { Title } = Typography;

// Password validation function (same as Register.js)
const validatePassword = (password) => {
  const hasMinLength = password.length >= 8;
  const hasCapitalLetter = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  
  if (!hasMinLength) return 'Password must be at least 8 characters long';
  if (!hasCapitalLetter) return 'Password must contain at least one capital letter';
  if (!hasNumber) return 'Password must contain at least one number (0-9)';
  if (!hasSpecialChar) return 'Password must contain at least one special character';
  
  return null;
};

const ResetPassword = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');
  const encodedEmail = searchParams.get('email');
  const email = encodedEmail ? decodeURIComponent(encodedEmail) : null;

  useEffect(() => {
    console.log('Token:', token);
    console.log('Encoded email:', encodedEmail);
    console.log('Decoded email:', email);
    
    if (!token || !email) {
      message.error('Invalid reset link. Please request a new password reset.');
      navigate('/forgot-password');
    } else {
      // Set the email in the form
      form.setFieldsValue({
        email: email
      });
    }
  }, [token, email, navigate, form]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      await authService.resetPassword({
        token,
        email,
        password: values.password,
        password_confirmation: values.password_confirmation
      });
      message.success('Password has been successfully reset!');
      navigate('/login');
    } catch (error) {
      message.error(
        error.response?.data?.message ||
        'Failed to reset password. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!token || !email) {
    return null;
  }

  return (
    <div className="login-container">
      <div className="login-content">
        {/* MaveshiBids Logo */}
        <div className="logo-section" style={{ textAlign: 'center', marginBottom: '30px' }}>
          <img 
            src="/assets/images/logo.png" 
            alt="MaveshiBids" 
            style={{ 
              height: '60px', 
              cursor: 'pointer',
              transition: 'transform 0.2s ease'
            }}
            onClick={() => navigate('/')}
            onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
            onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
          />
       
        </div>

        <div className="login-header">
          <Title level={2}>Reset Password</Title>
          <p>Enter your new password below</p>
        </div>
        <Form
          form={form}
          name="reset-password"
          onFinish={onFinish}
          layout="vertical"
          className="login-form"
        >
          <Form.Item
            name="email"
            label="Email"
          >
            <Input
              prefix={<MailOutlined className="input-icon" />}
              disabled
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="password"
            label="New Password"
            rules={[
              { required: true, message: 'Please input your new password!' },
              { min: 8, message: 'Password must be at least 8 characters!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value) return Promise.resolve();
                  const error = validatePassword(value);
                  if (error) {
                    return Promise.reject(new Error(error));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            extra="Password must be at least 8 characters long, contain one capital letter, one number, and one special character"
          >
            <Input.Password
              prefix={<LockOutlined className="input-icon" />}
              placeholder="Enter your new password"
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="password_confirmation"
            label="Confirm New Password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm your new password!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="input-icon" />}
              placeholder="Confirm your new password"
              size="large"
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={loading}
            >
              Reset Password
            </Button>
          </Form.Item>
          <div className="form-footer">
            <Button type="link" onClick={() => navigate('/login')} className="login-link">
              Back to Login
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default ResetPassword; 